import { Axis, Chart, Legend, LineAdvance, Tooltip } from 'bizcharts';
import { Badge, Button, CircularProgress, CircularProgressLabel, HStack, Heading, Menu, MenuButton, MenuItem, MenuList, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverHeader, PopoverTrigger, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { HiOutlineArrowTrendingDown, HiOutlineArrowTrendingUp } from 'react-icons/hi2';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { AppDispatch } from '../../store';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiCalendar } from 'react-icons/fi';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { deliveryType } from '../../types';
import { filterDeliveryData } from '../../store/actions';
import { getTotal } from 'utils/index';
import moment from 'moment';

type chartViewProps = {
  deliveries: Array<deliveryType>;
};

export const ChartView = (props: chartViewProps) => {

  const { onOpen, onClose, isOpen } = useDisclosure();

  let { deliveries = [] } = props;

  const [years] = useState<string[]>(['All', '2021', '2022', '2023', '2024']);
  const [selectedYear, setYear] = useState<string | any>(moment().format('YYYY'));

  const [dataFilter] = useState<string[]>(['week', 'month', 'year']);
  const [selectedFilter, setSelectedFilter] = useState<'week' | 'month' | 'year' | 'custom'>('week');

  const [selectedDates, setSelectedDates] = useState<Date[]>([
    new Date(),
    new Date(),
  ]);

  deliveries = selectedYear === 'All' ? deliveries : deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY') === selectedYear);

  const { filteredAmount } = useAppSelector(state => state.deliveryReducer);
  const { deliveredTotal, awaitApprovalTotal, approvedTotal, cancelledTotal, total } = filteredAmount;

  const dispatch: AppDispatch = useAppDispatch();

  //eslint-disable-next-line
  const [deliveredAmount, setDeliveredAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [upcomingAmount, setUpcomingAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [activeAmount, setActiveAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [cancelledAmount, setCancelledAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [approvedAmount, setApprovedAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [awaitApproval, setAwaitApprovalAmount] = useState<number>(0);
  //eslint-disable-next-line
  const [maxDeliveryStatus, setMaxDeliveryStatus] = useState<string>('delivered');

  const [deliveriesStatusAmount] = useState<Array<number>>([deliveredAmount, upcomingAmount, activeAmount, cancelledAmount, approvedAmount, awaitApproval]);

  //eslint-disable-next-line
  const maxValue = Math.max(...deliveriesStatusAmount);

  const newData = deliveries.map((delivery: deliveryType) => { return { ...delivery, date: moment(`${delivery.date}`).format('MMMM') }; });

  const getMonthlyDeliveries = (month: string) => {
    const monthlyDelivered = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'delivered');
    const delivered = monthlyDelivered.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(delivered === maxValue) { setMaxDeliveryStatus('delivered'); }
    // setDeliveredAmount(delivered);

    const monthlyUpcoming = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'upcoming');
    const upcoming = monthlyUpcoming.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(upcoming === maxValue) { setMaxDeliveryStatus('upcoming'); }
    // setUpcomingAmount(upcoming);

    const monthlyActive = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'active');
    const active = monthlyActive.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(active === maxValue) { setMaxDeliveryStatus('active'); }
    // setActiveAmount(active);

    const monthlyCancelled = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'cancelled');
    const cancelled = monthlyCancelled.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(cancelled === maxValue) { setMaxDeliveryStatus('cancelled'); }
    // setCancelledAmount(cancelled);

    const monthlyApproved = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'approved');
    const approved = monthlyApproved.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(approved === maxValue) { setMaxDeliveryStatus('approved'); }
    // setApprovedAmount(approved);

    const monthlyAwaitApproval = newData.filter((delivery: deliveryType) => delivery.date?.toLowerCase() === month.toLowerCase() && delivery.status === 'await approval');
    const awaitApproval = monthlyAwaitApproval.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);
    // if(awaitApproval === maxValue) { setMaxDeliveryStatus('await approval'); }
    // setAwaitApprovalAmount(awaitApproval);

    const total = deliveries.reduce((previousValue: any, currentValue: any) => previousValue + currentValue.total, 0);

    return {
      total,
      month,
      delivered,
      upcoming,
      active,
      cancelled,
      approved,
      awaitApproval
    };
  };

  const data: { month: string; status: string; amount: number | string; }[] = [
    {
      month: 'Jan',
      status: 'Delivered',
      amount: getMonthlyDeliveries('january').delivered
    },
    {
      month: 'Jan',
      status: 'Approved',
      amount: getMonthlyDeliveries('january').approved
    },
    {
      month: 'Jan',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('january').cancelled
    },
    {
      month: 'Feb',
      status: 'Delivered',
      amount: getMonthlyDeliveries('february').delivered
    },
    {
      month: 'Feb',
      status: 'Approved',
      amount: getMonthlyDeliveries('february').approved
    },
    {
      month: 'Feb',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('february').cancelled
    },
    {
      month: 'Mar',
      status: 'Delivered',
      amount: getMonthlyDeliveries('march').delivered
    },
    {
      month: 'Mar',
      status: 'Approved',
      amount: getMonthlyDeliveries('march').approved
    },
    {
      month: 'Mar',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('march').cancelled
    },
    {
      month: 'Apr',
      status: 'Delivered',
      amount: getMonthlyDeliveries('april').delivered
    },
    {
      month: 'Apr',
      status: 'Approved',
      amount: getMonthlyDeliveries('april').approved
    },
    {
      month: 'Apr',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('april').cancelled
    },
    {
      month: 'May',
      status: 'Delivered',
      amount: getMonthlyDeliveries('may').delivered
    },
    {
      month: 'May',
      status: 'Approved',
      amount: getMonthlyDeliveries('may').approved
    },
    {
      month: 'May',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('may').cancelled
    },
    {
      month: 'June',
      status: 'Delivered',
      amount: getMonthlyDeliveries('june').delivered
    },
    {
      month: 'June',
      status: 'Approved',
      amount: getMonthlyDeliveries('june').approved
    },
    {
      month: 'June',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('june').cancelled
    },
    {
      month: 'July',
      status: 'Delivered',
      amount: getMonthlyDeliveries('july').delivered
    },
    {
      month: 'July',
      status: 'Approved',
      amount: getMonthlyDeliveries('july').approved
    },
    {
      month: 'July',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('july').cancelled
    },
    {
      month: 'Aug',
      status: 'Delivered',
      amount: getMonthlyDeliveries('august').delivered
    },
    {
      month: 'Aug',
      status: 'Approved',
      amount: getMonthlyDeliveries('august').approved
    },
    {
      month: 'Aug',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('august').cancelled
    },
    {
      month: 'Sept',
      status: 'Delivered',
      amount: getMonthlyDeliveries('september').delivered
    },
    {
      month: 'Sept',
      status: 'Approved',
      amount: getMonthlyDeliveries('september').approved
    },
    {
      month: 'Sept',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('september').cancelled
    },
    {
      month: 'Oct',
      status: 'Delivered',
      amount: getMonthlyDeliveries('october').delivered
    },
    {
      month: 'Oct',
      status: 'Approved',
      amount: getMonthlyDeliveries('october').approved
    },
    {
      month: 'Oct',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('october').cancelled
    },
    {
      month: 'Nov',
      status: 'Delivered',
      amount: getMonthlyDeliveries('november').delivered
    },
    {
      month: 'Nov',
      status: 'Approved',
      amount: getMonthlyDeliveries('november').approved
    },
    {
      month: 'Nov',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('november').cancelled
    },
    {
      month: 'Dec',
      status: 'Delivered',
      amount: getMonthlyDeliveries('december').delivered
    },
    {
      month: 'Dec',
      status: 'Approved',
      amount: getMonthlyDeliveries('ecember').approved
    },
    {
      month: 'Dec',
      status: 'Cancelled',
      amount: getMonthlyDeliveries('december').cancelled
    }
  ];

  const getFilterRangeChange = (filter: 'week' | 'month' | 'year' | 'custom', status: 'delivered' | 'await approval' | 'approved' | 'cancelled') => {
    let currentStartDate = '';
    let currentEndDate = '';
    let prevStartDate = '';
    let prevEndDate = '';
    let progress: 'increase' | 'decrease' = 'increase';
    let amount: number = 0;
    const _deliveries = deliveries.filter((delivery: deliveryType) => delivery?.status === status);

    switch (filter) {
      case 'week':
        {
          currentStartDate = moment().startOf('isoWeek').format('YYYY-MM-DD');
          currentEndDate = moment().endOf('isoWeek').format('YYYY-MM-DD');
          prevStartDate = moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD');
          prevEndDate = moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD');
          const currentProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= currentStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= currentEndDate);
          const prevProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= prevStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= prevEndDate);
          const currentTotal = getTotal(currentProgress);
          const prevTotal = getTotal(prevProgress);
          amount = Number(currentTotal) - Number(prevTotal);
          progress = currentProgress?.length >= prevProgress?.length ? 'increase' : 'decrease';
          break;
        }
      case 'month':
        {
          currentStartDate = moment().startOf('months').format('YYYY-MM-DD');
          currentEndDate = moment().endOf('months').format('YYYY-MM-DD');
          prevStartDate = moment().subtract(1, 'months').startOf('months').format('YYYY-MM-DD');
          prevEndDate = moment().subtract(1, 'months').endOf('months').format('YYYY-MM-DD');
          const currentProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= currentStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= currentEndDate);
          const prevProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= prevStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= prevEndDate);
          const currentTotal = getTotal(currentProgress);
          const prevTotal = getTotal(prevProgress);
          amount = Number(currentTotal) - Number(prevTotal);
          progress = currentProgress?.length >= prevProgress?.length ? 'increase' : 'decrease';
          break;
        }
      case 'year':
        {
          currentStartDate = moment().startOf('years').format('YYYY-MM-DD');
          currentEndDate = moment().endOf('years').format('YYYY-MM-DD');
          prevStartDate = moment().subtract(1, 'years').startOf('years').format('YYYY-MM-DD');
          prevEndDate = moment().subtract(1, 'years').endOf('years').format('YYYY-MM-DD');
          const currentProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= currentStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= currentEndDate);
          const prevProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= prevStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= prevEndDate);
          const currentTotal = getTotal(currentProgress);
          const prevTotal = getTotal(prevProgress);
          amount = Number(currentTotal) - Number(prevTotal);
          progress = currentProgress?.length >= prevProgress?.length ? 'increase' : 'decrease';
          break;
        }
      case 'custom':
        {
          currentStartDate = moment(selectedDates[0]).format('YYYY-MM-DD');
          currentEndDate = moment(selectedDates[1]).format('YYYY-MM-DD');
          const currentProgress: deliveryType[] = _deliveries.filter((delivery: deliveryType) => moment(delivery?.date).format('YYYY-MM-DD') >= currentStartDate && moment(delivery?.date).format('YYYY-MM-DD') <= currentEndDate);
          const currentTotal = getTotal(currentProgress);
          amount = Number(currentTotal);
          progress = currentProgress?.length >= 0 ? 'increase' : 'decrease';
          break;
        }
      default:
        break;
    }

    return {
      amount: amount?.toFixed(2),
      progress,
      _deliveries,
      currentStartDate,
      currentEndDate
    };

  };

  const handleFilter = (filter: 'week' | 'month' | 'year' | 'custom') => {
    dispatch(filterDeliveryData(filter, selectedDates));
  };

  useEffect(() => { }, []);

  // useEffect(() => {
  //   dispatch(filterDeliveryData('week'));
  // }, [selectedFilter]);

  return (
    <Stack pt={4}>

      {/* <AreaChart width={chartWidth} height={250} data={chartData}
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="month" style={{ textTransform: 'capitalize' }} />
        <YAxis dataKey='total' color='#43a047' style={{ color: '#43a047' }} />
        <CartesianGrid strokeDasharray="3 3" />
        <Area type="monotone" dataKey="total" stroke="#4caf50" fillOpacity={1} fill="url(#colorUv)" />
      </AreaChart> */}
      <HStack justifyContent={'space-between'} display={{ base: '', md: 'flex' }}>
        <Stack h='100%' bg={'white'} p={4} mb={{ base: 4, md: 0 }} borderRadius={6} id='chart-container' w={{ base: '100%', md: '70%' }} boxShadow={'lg'}>
          <HStack spacing={3} justifyContent={'space-between'}>
            <Heading size={'sm'}>Chart Analytics</Heading>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                {selectedYear}
              </MenuButton>
              <MenuList minW={'95px'}>
                {
                  years.map((year: string, index: number) => <MenuItem key={index} onClick={() => setYear(year)}>{year}</MenuItem>)
                }
              </MenuList>
            </Menu>
          </HStack>
          <Chart
            padding={[50, 20, 20, 50]}
            autoFit
            height={300}
            data={data}
            scale={{
              amount: {
                nice: true
              }
            }}
          >
            <Axis
              name='amount'
              label={{
                formatter: (val: number | string) => {
                  if (Number(val) / 100 % 10 == 0 && val != 0) {
                    return `R ${(Number(val) / 1000)}K`;
                  }
                  else {
                    return `R ${val}`;
                  }
                }
              }}
            />
            <Legend
              visible={true}
              offsetY={10}
              marker={{
                symbol: 'hyphen'
              }}
              itemName={{
                style: {
                  textTransform: 'capitalize'
                }
              }}
              position='top'
            />
            <Tooltip
              showTitle={false}
              useHtml
              htmlContent={(_: any, items: any) => {
                const { amount, status } = items[0];
                return `<div class="target-tooltip" style='position:absolute;'><div class="target-tooltip-title">GMV</div><div>${status}: R${amount}</div></div>`;
              }}
            />
            <LineAdvance
              shape={'smooth'}
              point
              area
              position='month*amount'
              color={['status', (xVal) => {
                if (xVal === 'Delivered') {
                  return '#2e7d32';
                } else if (xVal === 'Approved') {
                  return '#ffc107';
                } else if (xVal === 'Cancelled') {
                  return '#e53935';
                }
                return '#EF7A39';
              }]}
            />
          </Chart>
        </Stack>
        <Stack bg={'white'} p={4} borderRadius={6} w={{ base: '100%', md: '29%' }} boxShadow={'lg'} mb={{ base: 6, md: 0 }} ml={'0 !important'}>
          <Heading size={'xs'}>Filter range: <Badge p={1} colorScheme={'teal'} textTransform={'capitalize'} variant={'outline'} borderRadius={'md'}>{moment(getFilterRangeChange(selectedFilter, 'delivered')?.currentStartDate).format('DD MMM YYYY')} - {moment(getFilterRangeChange(selectedFilter, 'delivered')?.currentEndDate).format('DD MMM YYYY')}</Badge></Heading>
          <HStack bg={'#ebeff1'} p={1} borderRadius={6} justifyContent={'space-between'}>
            {
              dataFilter.map((filter: 'week' | 'month' | 'year' | any, index: number) => (
                <HStack key={index} justifyContent={'center'} bg={selectedFilter === filter ? 'white' : 'transparent'} px={2} borderRadius={4} shadow={selectedFilter === filter ? 'sm' : 'none'} w={'100%'} textAlign={'center'} cursor={'pointer'} onClick={() => { setSelectedFilter(filter); handleFilter(filter); }}>
                  <Text fontSize={'sm'} textTransform={'capitalize'} color={selectedFilter === filter ? 'gray.900' : 'gray.600'} fontWeight={selectedFilter === filter ? 'medium' : 'regular'}>{filter}</Text>
                </HStack>
              ))
            }
            <Popover
              isOpen={isOpen}
              onOpen={onOpen}
              onClose={onClose}
            >
              <PopoverTrigger>
                <HStack justifyContent={'center'} bg={selectedFilter === 'custom' ? 'white' : 'transparent'} px={2} borderRadius={4} shadow={selectedFilter === 'custom' ? 'sm' : 'none'} w={'100%'} textAlign={'center'} cursor={'pointer'} onClick={() => { setSelectedFilter('custom'); }}>
                  <Text fontSize={'sm'} textTransform={'capitalize'} color={selectedFilter === 'custom' ? 'gray.900' : 'gray.600'} fontWeight={selectedFilter === 'custom' ? 'medium' : 'regular'}>Custom</Text>
                  <FiCalendar size={14} />
                </HStack>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontSize={'sm'}>Select start date  - end date!</PopoverHeader>
                <PopoverBody>
                  <Stack>
                    <Stack w='full' bgColor={'white'} borderRadius={6}>
                      <RangeDatepicker
                        selectedDates={selectedDates}
                        onDateChange={setSelectedDates}
                        propsConfigs={{
                          inputProps: {
                            w: '100%',
                            colorScheme: 'yellow',
                            _focusVisible: {
                              boxShadow: '0 0 0 1px #ffc107'
                            }
                          },
                          dayOfMonthBtnProps: {
                            selectedBtnProps: {
                              backgroundColor: 'yellow.400'
                            },
                            defaultBtnProps: {
                              _hover: {
                                backgroundColor: 'yellow.200'
                              }
                            }
                          }
                        }}
                      />
                      <Button colorScheme={'yellow'} onClick={() => { handleFilter('custom'); onClose(); }}>Submit</Button>
                    </Stack>
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
          <Stack p={2} bg={'gray.100'} borderRadius={6} spacing={2}>
            <HStack p={2} borderRadius={6} bg={'white'} justifyContent={'space-between'}>
              <Stack>
                <Text fontSize={'sm'} color={'green.500'} fontWeight={'medium'}>Delivered</Text>
                <HStack>
                  <Text fontWeight={'bold'} style={{ marginTop: '0' }}>R {deliveredTotal.toFixed(2)}</Text></HStack>
              </Stack>
              <Stack>
                <CircularProgress value={deliveredTotal === 0 ? 0 : (deliveredTotal / total) * 100} trackColor={'#cfd8dc'} color='green.400' size={'40px'}>
                  <CircularProgressLabel>{deliveredTotal === 0 ? 0 : ((deliveredTotal / total) * 100).toFixed(0)}%</CircularProgressLabel>
                </CircularProgress>
              </Stack>
            </HStack>
            <HStack p={2} borderRadius={6} bg={'white'} justifyContent={'space-between'}>
              <Stack>
                <Text fontSize={'sm'} color={'blue.500'} fontWeight={'medium'}>Await Approval</Text>
                <HStack>
                  <Text fontWeight={'bold'} style={{ marginTop: '0' }}>R {awaitApprovalTotal.toFixed(2)}</Text></HStack>
              </Stack>
              <Stack>
                <CircularProgress value={awaitApprovalTotal === 0 ? 0 : ((awaitApprovalTotal / total) * 100)} trackColor={'#cfd8dc'} color='blue.400' size={'40px'}>
                  <CircularProgressLabel>{awaitApprovalTotal === 0 ? 0 : ((awaitApprovalTotal / total) * 100).toFixed(0)}%</CircularProgressLabel>
                </CircularProgress>
              </Stack>
            </HStack>
            <HStack p={2} borderRadius={6} bg={'white'} justifyContent={'space-between'}>
              <Stack>
                <Text fontSize={'sm'} color={'yellow.500'} fontWeight={'medium'}>Approved</Text>
                <HStack>
                  <Text fontWeight={'bold'} style={{ marginTop: '0' }}>R {approvedTotal.toFixed(2)}</Text></HStack>
              </Stack>
              <Stack>
                <CircularProgress value={approvedTotal === 0 ? 0 : (approvedTotal / total) * 100} trackColor={'#cfd8dc'} color='yellow.400' size={'40px'}>
                  <CircularProgressLabel>{approvedTotal === 0 ? 0 : ((approvedTotal / total) * 100).toFixed(0)}%</CircularProgressLabel>
                </CircularProgress>
              </Stack>
            </HStack>
            <HStack p={2} borderRadius={6} bg={'white'} justifyContent={'space-between'}>
              <Stack>
                <Text fontSize={'sm'} color={'red.500'} fontWeight={'medium'}>Cancelled</Text>
                <HStack>
                  <Text fontWeight={'bold'} style={{ marginTop: '0' }}>R {cancelledTotal.toFixed(2)}</Text>
                </HStack>
              </Stack>
              <Stack>
                <CircularProgress value={cancelledTotal === 0 ? 0 : (cancelledTotal / total) * 100} trackColor={'#cfd8dc'} color='red.400' size={'40px'}>
                  <CircularProgressLabel>{cancelledTotal === 0 ? 0 : ((cancelledTotal / total) * 100).toFixed(0)}%</CircularProgressLabel>
                </CircularProgress>
              </Stack>
            </HStack>
          </Stack>
        </Stack>
      </HStack>
    </Stack>
  );
};